import Head from 'next/head';
import { useState, useEffect, useMemo, useRef } from 'react';
import { DialogTrigger, Heading } from 'react-aria-components';
import dynamic from 'next/dynamic';
import { string, object, bool, func, any } from 'prop-types';
import { useRouter } from 'next/router';
import { Button, Modal, Input } from 'antd';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { findStores } from 'actions/products';
import { useI18n } from 'context/i18n';
import { getIsMobile, getMarket } from 'utils/helpers';
import { LocationSVG } from 'components/iconsSVG/location';

import { HeadSEO } from 'components/base/headSEO';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { checkPostalCode } from 'utils/storeUtils';
import { IS_NNORMAL, IS_CAMPER } from 'utils/constants/system';
import { KEYCODES } from 'utils/constants/ux';
import { RETURN_TO_STORE } from 'utils/constants/shippings';
import { useLocale } from 'hooks';
import { geoSearch } from 'actions/geo-search';

import { LabSizeSelect } from 'components/pdp/lab-purchase-col';

import styles from './style.module.css';

const MapPinSVG = dynamic(() => import('components/iconsSVG/mapPin').then((mod) => mod.MapPinSVG));
const BurgerMenuSVG = dynamic(() => import('components/iconsSVG/burgerMenu').then((mod) => mod.BurgerMenuSVG));
const CamperThumbnailSVG = dynamic(() => import('components/iconsSVG/camperThumbnail').then((mod) => mod.CamperThumbnailSVG));
const NnormalSVG = dynamic(() => import('components/iconsSVG/nnormal').then((mod) => mod.NnormalSVG));
const ChevronLeftSVG = dynamic(() => import('components/iconsSVG/chevronLeft').then((mod) => mod.ChevronLeftSVG));
const GoToStoreOutlineSVG = dynamic(() => import('components/iconsSVG/goToStoreOutline').then((mod) => mod.GoToStoreOutlineSVG));
const AccountFaceOutlineSVG = dynamic(() => import('components/iconsSVG/accountFaceOutline').then((mod) => mod.AccountFaceOutlineSVG));
const WhatsappOutlineSVG = dynamic(() => import('components/iconsSVG/whatsappOutline').then((mod) => mod.WhatsappOutlineSVG));
const PhoneSquareOutlineSVG = dynamic(() => import('components/iconsSVG/phoneSquareOutline').then((mod) => mod.PhoneSquareOutlineSVG));
const CloseModalSVG = dynamic(() => import('components/iconsSVG/close').then((mod) => mod.CloseModalSVG));

const ProgressiveImg = dynamic(() => import('components/base/image').then((mod) => mod.ProgressiveImg));
const AppointmentModal = dynamic(() => import('components/appointmentModal'));
const SizesList = dynamic(() => import('components/sizesList'));

import { Drawer, DrawerBody, DrawerClose, DrawerFooter, DrawerHeader, DrawerRoot, DrawerX } from 'components/ui/drawer';
import { Input as AriaInput } from 'components/ui/field';
import { Button as AriaButton } from 'components/ui/button';

import { getDomain, getDomainValue, isDomain } from 'utils/domain';
import { Link } from 'components/ui/link';

const AppSVG = IS_NNORMAL ? NnormalSVG : CamperThumbnailSVG;

// deberían ser keys
const defaultAddressMessage = 'enter.a.location';
const geoErrorAddressMessage = 'error.geolocation';
const sizeMessages = {
  0: '',
  1: 'Queda un artículo',
  2: 'Quedan pocos',
  3: 'Disponible',
};
const errorMessages = {
  'no.products': 'Sorry, we could not find any stores.',
  'select.size': 'Please, select a size first.',
};

function StoreItem({ store, selectedSize, handleViewStoreInMap, whatsappPhone, takeback = false, onSelectStore = () => true, source = 'takeback' }) {
  const [showAppointment, setShowAppointment] = useState(false);
  const [loading, setLoading] = useState(false);
  const isMobile = getIsMobile('1023.5px');
  const { t, locale } = useI18n();
  const getQuantityFromSize = (sizeValue) => {
    if (sizeValue !== null && store.sizes !== undefined) {
      const [{ quantity }] = store.sizes.filter((item) => item.value === sizeValue);
      return quantity;
    }
    return (store.size = null);
  };

  const defaultQuantity = getQuantityFromSize(selectedSize);
  const [sizeQuantity, setSizeQuantity] = useState(defaultQuantity);

  const onClickSelectStore = () => {
    if (takeback && onSelectStore) {
      setLoading(true);
      onSelectStore({ ...store, returnMethod: RETURN_TO_STORE, loader: setLoading });
    }
  };

  const market = getMarket(locale);
  const parentLabel = source;
  let logo = <AppSVG />;

  if (IS_NNORMAL && source === 'no.trace.program') {
    logo = <img src="/assets-new/no_trace_program/nn-minilogo.png" alt="nnormal logo" width={21} />;
  }

  const preSelectedSize = store?.sizes?.find((item) => item.value === selectedSize);
  const defaultSelectedSize = preSelectedSize?.quantity > 0 ? selectedSize : null;

  return (
    <div className={`${styles.dispoStore} lab:border-none`}>
      <div
        className={`${styles.storeContent} ${takeback ? styles.storeContentTakeback : ''} ${source === 'no.trace.program' ? styles.storeContentNnormal : ''}`}
      >
        <div className={styles.dataColumn}>
          <h3 className={styles.titleWrapper}>
            {!isMobile && logo}
            <span className={styles.storeName}>{store.name}</span>
          </h3>

          <div className={`${styles.addressWrapper} lab:mb-4 lab:pl-0`}>
            <p>
              {store.address1}
              <br />
              {store.postalCode}, {store.cityName}
            </p>
            <span onClick={() => handleViewStoreInMap(store.latitude, store.longitude)} className={`${styles.viewInMap} ${styles.clickable}`}>
              {t('tiendas.locator', 'view.in.map', 'View in map')}
            </span>
          </div>
        </div>
        {store.size !== null ?
          <div className={styles.sizeColumn}>
            <p className={styles.columnTitle}>{t('ficha.producto', 'sizes', 'SIZES')}</p>
            <SizesList
              sizes={store.sizes.map((item) => ({ ...item, available: item.quantity > 0 }))}
              defaultSelectedSize={defaultSelectedSize}
              onChange={(item) => {
                const newQuantity = getQuantityFromSize(item);
                setSizeQuantity(newQuantity);
              }}
              grid={{
                gutter: 0,
                column: 3,
              }}
              select={IS_CAMPER && market === 'US'}
              currentCountry={market}
            />
            <p className={`${styles.sizeMessage} ${sizeQuantity > 2 ? styles.green : styles.yellow}`}>{sizeMessages[sizeQuantity]}</p>
          </div>
        : null}
        {takeback && (
          <div className={styles.selectStoreDiv}>
            <Button onClick={onClickSelectStore} loading={loading} type="default">
              {t(parentLabel, 'select.store', 'Select Store')}
            </Button>
          </div>
        )}
        {source !== 'no.trace.program' && (
          <div className={`${styles.scheduleColumn} `}>
            <p className={`${styles.columnTitle} lab:ml-0`}>{t('ficha.producto', 'opening.hours', 'OPENING HOURS')}</p>
            {store.storeHours.length > 0 && (
              <ul className={styles.schedule}>
                {store.storeHours.map((item, index) => (
                  <li key={`sch-${store.name}-${index}`} className={styles.schedulePairWrapper}>
                    <span>{item.days}</span>
                    <span>{item.timetable}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <div className={takeback ? `${styles.footer} ${styles.footerTakeback}` : styles.footer}>
        {store.telephone && (
          <div className={`${styles.clickable} ${styles.phone} lab:ml-0`}>
            <a href={`tel:${store.telephone}`}>
              <PhoneSquareOutlineSVG />
              <span>{store.telephone}</span>
            </a>
          </div>
        )}
        {whatsappPhone && (
          <div className={styles.clickable}>
            <a href={`https://api.whatsapp.com/send?phone=${whatsappPhone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">
              <WhatsappOutlineSVG />
              <span>{whatsappPhone}</span>
            </a>
          </div>
        )}
        {isMobile && <span className={styles.footerDivider}>|</span>}
        {store.bookingAvailability && (
          <div onClick={() => setShowAppointment(true)} className={`${styles.clickable} ${styles.tour}`}>
            <a>
              <AccountFaceOutlineSVG />
              <span>{t('generico', 'book.appointment', 'Book your appointment')}</span>
            </a>
          </div>
        )}
        <div className={`${styles.clickable} ${styles.goToStore}`}>
          <a href={store.urlGoogleMaps} target="_blank" rel="noreferrer">
            <GoToStoreOutlineSVG />
            <span>{t('generico', 'go.to.store', 'GO TO STORE')}</span>
          </a>
        </div>
      </div>
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.RECAPTCHA_KEY}
        language={locale}
        useRecaptchaNet
        scriptProps={{
          async: false,
          defer: false,
          appendTo: 'body',
          nonce: undefined,
        }}
      >
        <AppointmentModal visible={showAppointment} onClose={() => setShowAppointment(false)} store={store} />
      </GoogleReCaptchaProvider>
    </div>
  );
}

StoreItem.propTypes = {
  store: any,
  selectedSize: string,
  handleViewStoreInMap: func,
  whatsappPhone: string,
  takeback: bool,
  onSelectStore: func,
  source: string,
};

function DispoModal({
  productSheet = {},
  visible = false,
  onClose = () => true,
  takeback = false,
  onClickSearchDropoff = () => true,
  onSelectStore = () => true,
  source = 'takeback',
}) {
  const { t } = useI18n();
  const { locale, language } = useLocale();
  const isMobile = getIsMobile('1023.5px');
  const router = useRouter();
  const { query } = router;
  const { sizes, images, code, nameBase } = productSheet;

  const [loading, setLoading] = useState(false);
  const [geoString, setGeoString] = useState('');
  const [selectedDispoSize, setSelectedDispoSize] = useState(query.size);
  const [addressMessage, setAddressMessage] = useState(t('ficha.producto', defaultAddressMessage));
  const [availableStores, setAvailableStores] = useState([]);
  const [showingMap, setShowingMap] = useState(false);
  const [viewStoreInMap, setViewStoreInMap] = useState(false);
  const [map, setMap] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentUrl, setCurrentUrl] = useState(null);
  const [startedDispoEvent, setStartedDispoEvent] = useState(false);
  const MapConstructor = useRef(null);

  const handleErrorStoreAvailabilityEvent = (messageForError) => {
    dataLayerHandleEvent({
      event: 'error_store_availability',
      event_category: 'in store availability',
      event_detail_1: messageForError,
      event_detail_2: '',
    });
  };

  useEffect(() => {
    const url = window.location.href;
    if (url.indexOf('north_sails') > -1) {
      const setUrl = url.substring(0, url.lastIndexOf('/'));
      const searchUrl = setUrl.replace('/north_sails', IS_CAMPER ? '/camper_x_north_sails' : '/north_sails');
      setCurrentUrl(searchUrl);
    } else {
      setCurrentUrl(url.substring(0, url.lastIndexOf('/')));
    }
  }, []);

  const handleGetLeaflet = async (callback) => {
    import('leaflet').then((mod) => {
      callback(mod);
    });
  };

  const generateMap = (Leaflet, center, storesCoords, zoom = 12) => {
    const newMap = new Leaflet.Map(styles.map, {
      center,
      zoom,
    });

    // v TEMPORAL PRE USAR COMPONENTE GENÉRICO v

    const tileProvider = {
      url: 'https://{s}.tile.jawg.io/jawg-light/{z}/{x}/{y}{r}.png?access-token={accessToken}',
      config: {
        attribution: null, // '<a href="http://jawg.io" title="Tiles Courtesy of Jawg Maps" target="_blank">&copy; <b>Jawg</b>Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        minZoom: 0,
        maxZoom: 22,
        accessToken: process.env.LEAFLET_TOKEN,
      },
    };

    const markerProvider = {
      iconUrl: '/assets-new/marker.png',
    };

    Leaflet.tileLayer(tileProvider.url, tileProvider.config).addTo(newMap);

    const markerConfig = {};

    if (markerProvider) {
      markerConfig.icon = Leaflet.icon({
        iconSize: [22, 32],
        iconAnchor: [11, 32],
        ...markerProvider,
      });
    }

    // ^ temporal ^

    for (let i = 0; i < storesCoords.length; i++) {
      Leaflet.marker(storesCoords[i], markerConfig).addTo(newMap);
    }

    if (MapConstructor.current === null) {
      MapConstructor.current = Leaflet;
    }

    setMap(newMap);
  };

  const handleRemoveMap = () => {
    if (map && map.getContainer && map.getContainer().firstChild !== null && map.off && map.remove) {
      map.off();
      map.remove();
    }
  };

  // const setShowMoreShoes = () => {
  //   const url = window.location.href;
  //   return url.substring(0, url.lastIndexOf('/'));
  // };

  useEffect(() => {
    if (viewStoreInMap) {
      // para que no genere mapa de nuevos
      setViewStoreInMap(false);
    } else if (availableStores) {
      if (showingMap) {
        const averagePointCoords = availableStores.reduce((a, b) => {
          const { longitude: xA, latitude: yA } = a;
          const { longitude: xB, latitude: yB } = b;
          return { longitude: (xA + xB) / 2, latitude: (yA + yB) / 2 };
        });
        const storesCoords = availableStores.map(({ latitude, longitude }) => [latitude, longitude]);
        const mapCenter = [averagePointCoords.latitude, averagePointCoords.longitude];

        if (MapConstructor.current === null) {
          handleGetLeaflet((mod) => {
            generateMap(mod.default, mapCenter, storesCoords, 12);
          });
        } else {
          handleRemoveMap();
          generateMap(MapConstructor.current, mapCenter, storesCoords, 12);
        }
      } else if (map !== null) {
        handleRemoveMap();
      }
    }
  }, [showingMap]);

  useEffect(() => {
    setShowingMap(false);
  }, [availableStores]);

  useEffect(() => {
    // igual que la parte superior, cuando arreglemos traducciones dejará de ser así
    // (que mire directamente si errorMessage === a la key de traducción)
    if (errorMessage === errorMessages['select.size']) {
      setErrorMessage(null);
    }
  }, [selectedDispoSize]);

  useEffect(() => {
    if (startedDispoEvent !== false) {
      dataLayerHandleEvent({
        event: 'start_store_availability',
        event_category: 'in store availability',
        event_detail_1: startedDispoEvent === true ? 'find my store' : startedDispoEvent,
        event_detail_2: '',
      });
    }
  }, [startedDispoEvent]);

  const handleClose = () => {
    setAvailableStores([]);
    setShowingMap(false);
    onClose();
  };

  const handleSelectDispoSize = (size) => {
    setStartedDispoEvent(size);
    setSelectedDispoSize(size);
  };

  const handleFindStores = async (coords, geo = false) => {
    if (selectedDispoSize !== undefined) {
      setLoading(true);
      const { latitude, longitude } = coords;
      const result = await findStores({
        lang: locale,
        productId: code,
        latitude,
        longitude,
        size: selectedDispoSize,
        takeback,
      });
      setLoading(false);

      if (result.length > 0) {
        dataLayerHandleEvent({
          event: 'store_availability',
          event_category: 'in store availability',
          event_detail_1: geo ? `${coords.latitude},${coords.longitude}` : geoString,
          event_detail_2: selectedDispoSize,
        });
        setStartedDispoEvent(false);
        setAvailableStores(result);
      } else {
        setErrorMessage(errorMessages['no.products']);
      }
    } else {
      setErrorMessage(errorMessages['select.size']);
    }
  };

  const handleGetLocation = () => {
    if (window && window.navigator && window.navigator.geolocation) {
      setStartedDispoEvent(true);
      dataLayerHandleEvent({
        event: 'try_store_availability',
        event_category: 'in store availability',
        event_detail_1: '',
        event_detail_2: '',
      });
      window.navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          handleFindStores(coords, true);
        },
        (err) => {
          console.error(err);
          handleErrorStoreAvailabilityEvent(geoErrorAddressMessage);
          setAddressMessage(t('ficha.producto', geoErrorAddressMessage));
        },
      );
    }
  };

  const handleFindGeo = async () => {
    if (selectedDispoSize === undefined) {
      setStartedDispoEvent(true);
      setErrorMessage(errorMessages['select.size']);
      return;
    }
    if (geoString === '') return;
    dataLayerHandleEvent({
      event: 'try_store_availability',
      event_category: 'in store availability',
      event_detail_1: geoString,
      event_detail_2: '',
    });
    setLoading(true);
    const searchParams = {
      q: geoString,
    };

    const isPostalCode = checkPostalCode(locale, geoString);

    if (isPostalCode) {
      const urlCountry = getMarket(locale).toLowerCase();

      delete searchParams.q;
      searchParams.postalcode = geoString;
      searchParams.country = urlCountry.toLowerCase();
    }
    const response = await geoSearch({ query: searchParams, limit: 1 });
    if (response.length === 0) {
      handleErrorStoreAvailabilityEvent(geoErrorAddressMessage);
      setAddressMessage(t('ficha.producto', geoErrorAddressMessage));
      setLoading(false);
      return;
    }
    const { lat, lon } = response[0];
    handleFindStores({ latitude: lat, longitude: lon });
  };

  const handleViewStoreInMap = async (latitude, longitude) => {
    const center = [latitude, longitude];
    const changeMapForStore = () => {
      handleRemoveMap();
      setViewStoreInMap(center);
      setShowingMap(true);
    };

    if (MapConstructor.current === null) {
      handleGetLeaflet((mod) => {
        MapConstructor.current = mod.default;
        changeMapForStore();
      });
    } else {
      changeMapForStore();
    }
  };

  useEffect(() => {
    if (Array.isArray(viewStoreInMap)) {
      // el centro y el único pin son lo mismo
      generateMap(MapConstructor.current, viewStoreInMap, [viewStoreInMap], 20);
    }
  }, [viewStoreInMap]);

  useEffect(() => {
    if (errorMessage !== null) {
      Object.entries(errorMessages).forEach(([errorKey, errorValue]) => {
        if (errorValue === errorMessage) {
          handleErrorStoreAvailabilityEvent(errorKey);
        }
      });
    }
  }, [errorMessage]);

  const parentLabel = source;

  const market = getMarket(locale);
  const FirstStep = useMemo(
    () => (
      <>
        {!takeback && (
          <ProgressiveImg src={images.find((image) => !image.isVideo)?.lg} alt={'Thumbnail image of'} className={`${styles.dispoImage} lab:hidden`} />
        )}
        <div className={`${styles.dispoContent} ${takeback ? styles.dispoContentTakeback : ''}`}>
          <p className={`${styles.dispoTitle} lab:text-left lab:text-[12px] lab:uppercase`}>
            {takeback ? t(parentLabel, 'store.locator.title', 'Store Locator') : t('ficha.producto', 'in.store.availability')}
          </p>

          {!takeback && sizes[0]?.value !== '' ?
            <div className={styles.dispoSizesContainer}>
              <p className={styles.dispoSubtitle}>{t('ficha.producto', 'what.size')}</p>
              {getDomainValue({
                camper: (
                  <SizesList
                    showLimitedSizeStyle={false}
                    sizes={sizes.map((item) => ({ ...item, available: true }))}
                    defaultSelectedSize={selectedDispoSize}
                    onChange={handleSelectDispoSize}
                    grid={{
                      gutter: 0,
                      column: 7,
                    }}
                    select={IS_CAMPER && market === 'US'}
                    currentCountry={market}
                    productSheet={productSheet}
                  />
                ),
                nnormal: 'camper',
                camperlab: (
                  <div className="m-auto block w-full">
                    <LabSizeSelect
                      sizes={sizes.map((item) => ({ ...item, available: true, quantity: 100 }))}
                      handleSelectSize={handleSelectDispoSize}
                      selectedSize={selectedDispoSize}
                      variant="default"
                    />
                  </div>
                ),
              })}
            </div>
          : setSelectedDispoSize(null)}

          <div className={styles.dispoInputContainer}>
            <p
              dangerouslySetInnerHTML={{
                __html: takeback ? t(parentLabel, 'store.locator.description', 'Enter your city or country to find your nearest store:') : addressMessage,
              }}
            ></p>
            <div className={styles.inputWithIconContainer}>
              {getDomainValue({
                camper: (
                  <>
                    <Input
                      onClick={() => (startedDispoEvent === false ? setStartedDispoEvent(true) : null)}
                      value={geoString}
                      onKeyDown={(e) => {
                        if (e.code === KEYCODES.enter) {
                          handleFindGeo();
                        }
                      }}
                      onChange={(e) => {
                        setGeoString(e.target.value);
                      }}
                      onFocus={() => {
                        if (errorMessage !== null) {
                          setErrorMessage(null);
                        }
                      }}
                      className={styles.dispoInput}
                      type="text"
                      placeholder={
                        takeback ?
                          t(parentLabel, 'store.locator.input.placeholder', 'Type your location')
                        : t('ficha.producto', 'enter.your.location', 'Enter your location')
                      }
                    />
                    <Button onClick={handleGetLocation} className={styles.geoIcon} icon={<LocationSVG />} />
                  </>
                ),
                nnormal: 'camper',
                camperlab: (
                  <div className="flex w-full gap-2">
                    <AriaInput
                      variant="default"
                      value={geoString}
                      onChange={(e) => {
                        setGeoString(e.target.value);
                      }}
                      placeholder={
                        takeback ?
                          t(parentLabel, 'store.locator.input.placeholder', 'Type your location')
                        : t('ficha.producto', 'enter.your.location', 'Enter your location')
                      }
                      onKeyDown={(e) => {
                        if (e.code === KEYCODES.enter) {
                          handleFindGeo();
                        }
                      }}
                      onFocus={() => {
                        if (errorMessage !== null) {
                          setErrorMessage(null);
                        }
                      }}
                      className="flex w-full"
                      type="text"
                    />
                    <AriaButton variant="none" onPress={handleGetLocation} className="absolute right-0 flex size-8 items-center justify-center lg:size-6">
                      <LocationSVG />
                    </AriaButton>
                  </div>
                ),
              })}
            </div>
            {errorMessage !== null && <span className={`error ${styles.inputError}`}>{errorMessage}</span>}
          </div>

          {getDomainValue({
            camper: (
              <Button
                loading={loading}
                onClick={handleFindGeo}
                type={takeback ? 'primary' : 'default'}
                className={takeback ? `${styles.takebackBtn} ${styles.takebackSearchBtn}` : styles.dispoFindButton}
              >
                {takeback ? t(parentLabel, 'search', 'Search') : t('ficha.producto', 'find.stores')}
              </Button>
            ),
            nnormal: 'camper',
            camperlab: (
              <div className="absolute bottom-0 flex w-[95%] flex-shrink-0 flex-col gap-2.5 bg-clear py-2.5">
                <AriaButton isPending={loading} variant="solid" onPress={handleFindGeo} className="">
                  {takeback ? t(parentLabel, 'search', 'Search') : t('ficha.producto', 'find.stores')}
                </AriaButton>
              </div>
            ),
          })}
        </div>
      </>
    ),
    [selectedDispoSize, geoString, loading, addressMessage, errorMessage],
  );

  const SecondStep = useMemo(
    () => (
      <>
        <div
          className={`${styles.dispoContent} relative h-[calc(100svh-160px)] ${styles.dispoContentAlone} ${takeback ? styles.dispoContentTakeback : ''} lab:p-0`}
        >
          <p className={`${styles.dispoTitle} mb-2 lab:self-start lab:text-left lab:text-sm lab:uppercase lab:lg:text-xs`}>
            {takeback ? t(parentLabel, 'select.your.store', 'Select your Store') : t('ficha.producto', 'in.store.availability')}
          </p>
          {takeback ?
            <p className={`${styles.dispoSubtitle} mb-2 lab:self-start lab:text-left lab:text-sm lab:lg:text-xs`}>
              <span>{t(parentLabel, 'cant.find.store', 'Can’t find a Camper store near you?')}</span>
              <span onClick={onClickSearchDropoff} className={styles.dispoSubtitleDropoff}>
                {t(parentLabel, 'search.dropoff.locations', 'Search available Drop-Off locations.')}
              </span>
            </p>
          : <p className={`${styles.dispoSubtitle} lab:self-start lab:text-left lab:text-sm lab:lg:text-xs`}>{t('ficha.producto', 'stock.may.vary')}</p>}

          <div
            className={
              takeback ?
                `${styles.dispoStoresContainer} ${source !== 'no.trace.program' ? styles.dispoStoresContainerTakeback : styles.dispoStoresContainerNnormal}`
              : styles.dispoStoresContainer
            }
          >
            {!isMobile &&
              !takeback &&
              getDomainValue({
                camper: (
                  <div className={styles.dispoTabsContainer}>
                    <Button
                      onClick={() => setShowingMap(false)}
                      autoFocus={true}
                      className={`${styles.dispoTab} ${showingMap ? '' : styles.active}`}
                      icon={<BurgerMenuSVG />}
                    >
                      {t('generico', 'view.list', 'View List')}
                    </Button>
                    <Button
                      onClick={() => setShowingMap(true)}
                      className={`${styles.dispoTab} ${showingMap ? styles.active : ''}`}
                      icon={<MapPinSVG />}
                    ></Button>
                  </div>
                ),
                nnormal: 'camper',
                camperlab: (
                  <div className="flex items-center justify-end gap-2">
                    <AriaButton
                      variant="none"
                      onClick={() => setShowingMap(false)}
                      autoFocus={true}
                      className="flex h-8 items-center justify-center px-1.5 text-sm lab:shadow-none lab:ring-opacity-0 lab:ring-offset-0 lg:h-6 lg:text-xs"
                      //className={`${styles.dispoTab} ${showingMap ? '' : styles.active}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
                        />
                      </svg>
                      {t('generico', 'view.list', 'View List')}
                    </AriaButton>
                    <AriaButton
                      variant="none"
                      onClick={() => setShowingMap(true)}
                      className="flex h-8 items-center justify-center px-1.5 text-sm lab:shadow-none lab:ring-opacity-0 lab:ring-offset-0 lg:h-6 lg:text-xs"
                      //className={`${styles.dispoTab} ${showingMap ? styles.active : ''}`}
                    >
                      {t('tiendas.locator', 'view.map')}
                    </AriaButton>
                  </div>
                ),
              })}
            <div id={styles.map} className={`${showingMap ? '' : 'escondido'}`}></div>
            <div className={`${styles.dispoStoresContent} ${showingMap ? 'escondido' : ''}`}>
              {availableStores.map((item, index) => (
                <StoreItem
                  source={source}
                  takeback={takeback}
                  onSelectStore={onSelectStore}
                  key={`store-${index}`}
                  store={item}
                  selectedSize={selectedDispoSize}
                  handleViewStoreInMap={handleViewStoreInMap}
                />
              ))}
            </div>
          </div>

          <div
            className={
              isDomain('camperlab') ? 'sticky inset-x-0 bottom-0 mt-auto flex w-full flex-col gap-1 bg-clear py-2'
              : language === 'en' ?
                `${styles.dispoStoresFooterEn} ${styles.dispoStoresFooter} ${takeback ? styles.dispoStoresFooterTakeback : ''}`
              : `${styles.dispoStoresFooter} ${takeback ? styles.dispoStoresFooterTakeback : ''} ${showingMap ? styles.dispoStoreFooterMap : ''}`
            }
          >
            {isMobile || takeback ?
              <>
                {getDomainValue({
                  camper: (
                    <Button
                      className={takeback ? `${styles.blackToRedButton} ${styles.takebackBlackToWhiteBtn}` : styles.blackToRedButton}
                      onClick={() => setShowingMap((currentShowingMap) => !currentShowingMap)}
                    >
                      {showingMap ? t('tiendas.locator', 'view.list') : t('tiendas.locator', 'view.map')}
                    </Button>
                  ),
                  nnormal: 'camper',
                  camperlab: (
                    <AriaButton onPress={() => setShowingMap((currentShowingMap) => !currentShowingMap)}>
                      {showingMap ? t('tiendas.locator', 'view.list') : t('tiendas.locator', 'view.map')}
                    </AriaButton>
                  ),
                })}
              </>
            : <>
                {getDomainValue({
                  camper: (
                    <Button className={styles.backButton} onClick={() => setAvailableStores([])}>
                      {t('mis.pedidos', 'atras')}
                    </Button>
                  ),
                  nnormal: 'camper',
                  camperlab: (
                    <AriaButton variant="outline" onPress={() => setAvailableStores([])}>
                      {t('mis.pedidos', 'atras')}
                    </AriaButton>
                  ),
                })}
              </>
            }
            {/* <Button>{t('listado.productos', 'ver.mas')} {nameBase}</Button> */}
            {/* <Button onClick={() => setShowMoreShoes()}>{t('listado.productos', 'ver.mas')} {nameBase}</Button> */}
            {!takeback &&
              getDomainValue({
                camper: (
                  <a href={currentUrl} className={styles.dispoShowMoreShoesButton}>
                    {t('listado.productos', 'ver.mas')} {nameBase}
                  </a>
                ),
                nnormal: 'camper',
                camperlab: (
                  <Link variant="solid" href={currentUrl}>
                    {t('listado.productos', 'ver.mas')} {nameBase}
                  </Link>
                ),
              })}
          </div>
        </div>
      </>
    ),
    [availableStores, showingMap, isMobile, MapConstructor, selectedDispoSize],
  );

  const dispoContent = (
    <>
      {(isMobile || takeback) && availableStores.length > 0 && (
        <Button className={styles.backArrow} icon={<ChevronLeftSVG className={styles.arrowIcon} />} onClick={() => setAvailableStores([])} />
      )}
      {availableStores.length === 0 ? FirstStep : SecondStep}
    </>
  );

  const modalWrapper = (
    <Modal
      className={`${styles.dispoModal} ${takeback ? styles.dispoModalTakeback : ''} ${availableStores.length === 0 && takeback ? styles.firstStepModal : ''} ${showingMap ? styles.mapModal : ''}`}
      width={takeback && availableStores.length === 0 ? '400px' : '800px'}
      title={null}
      visible={visible}
      centered={true}
      closable={true}
      destroyOnClose={true}
      onCancel={handleClose}
      footer={null}
      closeIcon={<CloseModalSVG />}
      wrapClassName={styles.dispoModalWrap}
    >
      {dispoContent}
    </Modal>
  );

  return (
    <>
      <Head>
        <link
          key="leaflet-css"
          rel="stylesheet"
          href="https://unpkg.com/leaflet@1.7.1/dist/leaflet.css"
          integrity="sha512-xodZBNTC5n17Xt2atTPuE1HxjVMSvLVW9ocqUKLsCC5CXdbqCmblAshOMAS6/keqq/sMZMZ19scR4PsZChSR7A=="
          crossOrigin=""
        />
        <script
          async
          key="leaflet-js"
          src="https://unpkg.com/leaflet@1.7.1/dist/leaflet.js"
          integrity="sha512-XQoYMqMTK8LvdxXYG3nZ448hOEQiglfqkJs1NOQV44cWnUrBc8PkAOcXy20w0vlaXaVUearIOBhiXZ5V3ynxwA=="
          crossOrigin=""
        ></script>
      </Head>
      {getDomainValue({
        camper: modalWrapper,
        nnormal: 'camper',
        camperlab: dispoContent,
      })}
    </>
  );
}

DispoModal.propTypes = {
  productSheet: object,
  visible: bool,
  onClose: func,
  takeback: bool,
  onClickSearchDropoff: func,
  onSelectStore: func,
  source: string,
};

export function DispoModalWithButton({ productSheet = {} }) {
  const { profileData, t } = useI18n();
  const [showDispoModal, setShowDispoModal] = useState(false);
  const { country: market } = useLocale();
  // const [ stockLink, setStockLink ] = useState(false);
  // const { isCountry } = useCountries();

  useEffect(() => {
    if (window) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('showstockoffline') === 'true') {
        setShowDispoModal(true);
      }
    }
    // if (window.location.href.indexOf('goods') > -1) {
    //   setStockLink(true);
    // }
  }, []);

  const handleShowDispoModal = () => {
    setShowDispoModal((currentValue) => !currentValue);
    // sabemos si el valor actual del showDispoModal es false es porque se va a abrir en este click
    if (!showDispoModal) {
      dataLayerHandleEvent({
        event: 'item_info',
        event_category: 'product detail',
        event_detail_1: 'store availability',
        event_detail_2: '',
      });
      dataLayerHandleEvent({
        event: 'stock_offline',
        item_name: productSheet?.brand?.toLowerCase() || '',
        item_id: productSheet?.code || '',
        store: market,
      });
    }
  };

  const dispoModalToUse = getDomainValue({
    camper: (
      <>
        {!productSheet.isCamperOne && profileData.stockInStore && !productSheet.recycled && (
          <button className="flex h-10 items-center gap-3 text-left text-xs underline" onClick={handleShowDispoModal}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" className="size-6">
              <path stroke="currentColor" d="M19 7A5 5 0 0 0 9 7" />
              <path stroke="currentColor" strokeLinecap="round" d="M4 12.106v-5h19v11.5c0 1.5-.9 2.5-2.5 2.5h-14c-1.5 0-2.5-.5-2.5-1.5" />
              <path
                stroke="currentColor"
                strokeLinecap="round"
                d="M1 12.335h3.611l3.13-2.967c.32-.313 1.39-1.227 2.166-.47.241.235.482.939-.24 1.877l-.963 1.56h4.092c.402 0 1.204.235 1.204 1.173v4.222c0 .625-.433 1.876-2.167 1.876H1M14 14.606H9M14 16.606H9"
              />
            </svg>
            {t('ficha.producto', 'check.in.store.availability')}
          </button>
        )}
        <DispoModal productSheet={productSheet} visible={showDispoModal} onClose={handleShowDispoModal} />
      </>
    ),
    nnormal: 'camper',
    camperlab: (
      <DialogTrigger>
        {!productSheet.isCamperOne && profileData.stockInStore && !productSheet.recycled && (
          <AriaButton variant="none" className="flex h-auto items-center gap-3 text-left text-xs underline" onClick={handleShowDispoModal}>
            {t('ficha.producto', 'check.in.store.availability')}
          </AriaButton>
        )}
        <DrawerRoot isOpen={showDispoModal} onOpenChange={handleShowDispoModal} className="w-full">
          <Drawer>
            <DrawerHeader label={t('ficha.producto', 'in.store.availability')} />
            <DrawerBody>
              <DispoModal productSheet={productSheet} visible={showDispoModal} onClose={handleShowDispoModal} />
            </DrawerBody>
          </Drawer>
        </DrawerRoot>
      </DialogTrigger>
    ),
  });

  return dispoModalToUse;
}

export default DispoModal;
